import { Home, LayoutGrid, Settings } from "lucide-react";
import { cn } from "@/modern/utils";
import { getViewStateIconByRecord } from "@/other/utils/utils.jsx";
import { VIEW_DEF_PREF, VIEW_TYPES } from "@/other/constants/OtherKeys";
import { getCount, VIEW_TO_STORE_KEY_MAP } from "@/other/utils/storeCountUtil.jsx";
import type { ISwivuProject } from "@/types/SwivuProject";
import { EntityFinderHighlight } from "@/modern/components/entity-finder/components/EntityFinderHighlight";
import { EntityFinderMetaBadge } from "@/modern/components/entity-finder/components/EntityFinderMetaBadge";
import { entityFinderIconShellClass, entityFinderRowClass } from "@/modern/components/entity-finder/components/EntityFinderItemRow";
import {
  ENTITY_FINDER_ROW_HEIGHT,
  ENTITY_FINDER_ROW_HEIGHT_NO_SUBTITLE,
  ENTITY_FINDER_SUBTITLE_CLS,
  ENTITY_FINDER_TITLE_CLS,
} from "@/modern/components/entity-finder/constants/layout";
import { navigateToAnonymousView } from "@/modern/components/entity-finder/constants/config/shared";
import type {
  EntityFinderConfigEntry,
  EntityFinderContext,
  EntityFinderItem,
  EntityFinderListEntry,
  EntityFinderViewRecord,
  NavItemRecord,
  ViewItemRecord,
} from "@/modern/components/entity-finder/types";
import {
  defaultItemSearch,
  defaultMatchesScope,
  matchesEntityFinderSearchQuery,
  searchModeActive,
} from "@/modern/components/entity-finder/utils";

const UNASSIGNED_FOLDER = "Unassigned";

type ViewDefPrefEntry = { name?: string; viewDescription?: string };

function getViewTypeLabel(viewType: string): string {
  return (VIEW_DEF_PREF as Record<string, ViewDefPrefEntry>)[viewType]?.name ?? viewType;
}

function getViewDescription(view: EntityFinderViewRecord): string {
  const custom = view.viewDescription?.trim();
  if (custom) return custom;
  const defaultDescription = (VIEW_DEF_PREF as Record<string, ViewDefPrefEntry>)[view.viewType]
    ?.viewDescription?.trim();
  return defaultDescription ?? "";
}

function buildFolderNameMap(project: ISwivuProject): Map<string | null, string> {
  const map = new Map<string | null, string>();
  map.set(null, UNASSIGNED_FOLDER);
  project.viewsFolderStore.query(() => true, true).forEach((folder) => {
    map.set(folder.id, folder.name);
  });
  return map;
}

function getViewCountForView(project: ISwivuProject, view: EntityFinderViewRecord): number | undefined {
  if (!view.isDefaultView) return undefined;
  const storeKey = VIEW_TO_STORE_KEY_MAP[view.viewType as keyof typeof VIEW_TO_STORE_KEY_MAP];
  if (!storeKey) return undefined;
  const store = project.getCrudStore(storeKey);
  if (!store) return undefined;
  return getCount(store, view.viewType);
}

function scoreViewMatch(view: EntityFinderViewRecord, folderName: string, query: string): number {
  const q = query.toLowerCase();
  const viewTypeLabel = getViewTypeLabel(view.viewType);
  let score = 0;
  if (view.name.toLowerCase().includes(q)) score += 10;
  else if (matchesEntityFinderSearchQuery(view.name, query)) score += 6;
  if (view.viewDescription?.toLowerCase().includes(q)) score += 4;
  if (folderName.toLowerCase().includes(q)) score += 3;
  if (viewTypeLabel.toLowerCase().includes(q)) score += 2;
  return score;
}

function mapViewToItem(project: ISwivuProject, view: EntityFinderViewRecord): EntityFinderItem<"views"> {
  const folderMap = buildFolderNameMap(project);
  const folderName = folderMap.get(view.folderId ?? null) ?? UNASSIGNED_FOLDER;
  const count = getViewCountForView(project, view);
  const record: ViewItemRecord = {
    kind: "view",
    viewId: view.id,
    iconId: view.iconId,
    color: view.color,
    isDefaultView: view.isDefaultView,
    viewType: view.viewType,
    count,
    category: folderName,
  };

  return {
    id: `view-${view.id}`,
    type: "views",
    label: view.name,
    subtitle: getViewDescription(view) || undefined,
    record,
  };
}

function queryBoardViews(ctx: EntityFinderContext): EntityFinderItem<"views">[] {
  const trimmed = ctx.debouncedQuery.trim();
  if (!trimmed) return [];

  const folderMap = buildFolderNameMap(ctx.project);
  const views = ctx.project.viewsStore.query(
    (view) => (view as unknown as EntityFinderViewRecord).isVisible,
    true
  ) as unknown as EntityFinderViewRecord[];

  const scored: Array<{ item: EntityFinderItem<"views">; score: number; order: number }> = [];

  for (const view of views) {
    const folderName = folderMap.get(view.folderId ?? null) ?? UNASSIGNED_FOLDER;
    const viewTypeLabel = getViewTypeLabel(view.viewType);
    const searchableText = [view.name, view.viewDescription, folderName, viewTypeLabel]
      .filter(Boolean)
      .join(" ");

    if (!matchesEntityFinderSearchQuery(searchableText, trimmed)) continue;

    scored.push({
      item: mapViewToItem(ctx.project, view),
      score: scoreViewMatch(view, folderName, trimmed),
      order: view.order ?? 0,
    });
  }

  return scored
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.order - b.order ||
        a.item.label.localeCompare(b.item.label)
    )
    .map((entry) => entry.item);
}

function queryNavItems(ctx: EntityFinderContext): EntityFinderItem<"views">[] {
  const trimmed = ctx.debouncedQuery.trim();
  if (!trimmed) return [];

  const items: EntityFinderItem<"views">[] = [
    {
      id: "nav-workspace-settings",
      type: "views",
      label: "Settings",
      subtitle: "Workspace settings",
      record: { kind: "nav", navId: "workspace-settings" } satisfies NavItemRecord,
    },
    {
      id: "nav-my-swivu",
      type: "views",
      label: "My Swivu",
      subtitle: "Personal dashboard",
      record: { kind: "nav", navId: "my-swivu" } satisfies NavItemRecord,
    },
  ];

  return defaultItemSearch(trimmed, items);
}

export const viewsConfig: EntityFinderConfigEntry<"views"> = {
  type: "views",
  label: "View",
  plural: "Views",
  icon: LayoutGrid,
  tint: "bg-chart-3/15 text-chart-3",
  listOrder: 0,
  showInFilter: true,
  isActive: searchModeActive,
  matchesScope: (ctx) => defaultMatchesScope(ctx, "views"),
  getData: (ctx) => [...queryBoardViews(ctx), ...queryNavItems(ctx)],
  getRecentItem: (ctx) => {
    const previousView = ctx.recentViews[1];
    if (!previousView?.isVisible) return null;
    return mapViewToItem(ctx.project, previousView);
  },
  buildListSection: (items, _ctx) => {
    if (items.length === 0) return [];
    const entries: EntityFinderListEntry[] = [{ type: "header", label: "Views", configType: "views" }];
    for (const item of items) {
      entries.push({ type: "item", item, configType: "views" });
    }
    return entries;
  },
  estimateRowHeight: (item) =>
    item.subtitle ? ENTITY_FINDER_ROW_HEIGHT : ENTITY_FINDER_ROW_HEIGHT_NO_SUBTITLE,
  itemRenderer: ({ item, query, ctx }) => {
    const record = item.record as ViewItemRecord | NavItemRecord;

    if (record.kind === "nav") {
      const Icon = record.navId === "my-swivu" ? Home : Settings;
      const hasSubtitle = Boolean(item.subtitle);
      return (
        <div className={entityFinderRowClass(hasSubtitle)}>
          <div className={entityFinderIconShellClass(hasSubtitle, "text-muted-foreground")}>
            <Icon size={16} aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className={cn("truncate", ENTITY_FINDER_TITLE_CLS)}>
              <EntityFinderHighlight text={item.label} query={query} />
            </div>
            {item.subtitle ? (
              <p className={cn("mt-0.5 truncate", ENTITY_FINDER_SUBTITLE_CLS)}>
                <EntityFinderHighlight text={item.subtitle} query={query} tone="muted" />
              </p>
            ) : null}
          </div>
        </div>
      );
    }

    const viewRecord = record;
    const iconCls = getViewStateIconByRecord(
      {
        iconId: viewRecord.iconId,
        isDefaultView: viewRecord.isDefaultView,
        viewType: viewRecord.viewType,
      },
      ctx.project
    );
    const showCount = viewRecord.isDefaultView && viewRecord.count != null && viewRecord.count > 0;
    const hasSubtitle = Boolean(item.subtitle);

    return (
      <div className={entityFinderRowClass(hasSubtitle)}>
        <div className={entityFinderIconShellClass(hasSubtitle)}>
          <i
            className={cn(iconCls, "text-base", !viewRecord.color && "text-muted-foreground")}
            style={viewRecord.color ? { color: viewRecord.color } : undefined}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2 overflow-hidden leading-5">
            <span className={cn("min-w-0 truncate", ENTITY_FINDER_TITLE_CLS)}>
              <EntityFinderHighlight text={item.label} query={query} />
            </span>
            {showCount ? <EntityFinderMetaBadge variant="muted">{viewRecord.count}</EntityFinderMetaBadge> : null}
          </div>
          {item.subtitle ? (
            <p className={cn("mt-0.5 truncate leading-5", ENTITY_FINDER_SUBTITLE_CLS)}>
              <EntityFinderHighlight text={item.subtitle} query={query} tone="muted" />
            </p>
          ) : null}
        </div>
        <EntityFinderMetaBadge
          variant="muted"
          className={cn(hasSubtitle && "mt-0.5")}
          title={viewRecord.category}
        >
          <EntityFinderHighlight text={viewRecord.category} query={query} tone="muted" />
        </EntityFinderMetaBadge>
      </div>
    );
  },
  onSelect: (item, ctx) => {
    const record = item.record as ViewItemRecord | NavItemRecord;

    if (record.kind === "nav") {
      if (record.navId === "workspace-settings") {
        navigateToAnonymousView(ctx, VIEW_TYPES.settings, "Settings", "settings");
      } else {
        navigateToAnonymousView(ctx, VIEW_TYPES.mySwivu, "My Swivu", VIEW_TYPES.mySwivu);
      }
      return;
    }

    if (!ctx.viewManager) return;
    const view = ctx.viewManager.getViewById(record.viewId);
    if (!view) return;
    ctx.viewManager.selectView(view);
    if (ctx.entityManager.isOpen) {
      ctx.entityManager.closeEntityManager();
    }
  },
};
